/**
 * Beneficial Owner Notices — Stage 진입 전 설문.
 * purposes(복수 가능) + 수신/통지 유형 → 포함 문서(docCode) 결정.
 */
import type { BoNoticeDocCode } from './boNoticeFields';
import { BO_NOTICE_DOCS } from './boNoticeFields';

export type BoNoticePurposeKind = 'identify' | 'maintain';

/** @deprecated 단일 purpose — purposes[] 로 이전. restore 호환용 */
export type BoNoticePurpose = BoNoticePurposeKind | '';

/** Stage 1 — 누구에게 / 어떤 통지 */
export type BoNoticeIdentifyOption =
  | 'corporate_member'
  | 'individual_member'
  | 'suspected_bo'
  | 'person_who_may_know';

/** Stage 2 — 변경 확인 vs 정확성 확인 */
export type BoNoticeMaintainOption = 'change' | 'confirm';

export interface BoNoticeSurvey {
  /** 목적 복수 선택 가능 (identify + maintain 동시) */
  purposes: BoNoticePurposeKind[];
  /** 레거시 단일 값 — normalizeSurvey 에서 purposes 로 이관 */
  purpose?: BoNoticePurpose;
  identifyOptions: BoNoticeIdentifyOption[];
  maintainOptions: BoNoticeMaintainOption[];
  completed: boolean;
}

export interface BoNoticeSurveyOptionMeta {
  id: string;
  label: string;
  hint: string;
  docCode: BoNoticeDocCode;
}

export const emptyBoNoticeSurvey = (): BoNoticeSurvey => ({
  purposes: [],
  identifyOptions: [],
  maintainOptions: [],
  completed: false,
});

/** 저장된 설문(구 purpose 단일값 포함)을 현재 스키마로 맞춤. */
export function normalizeSurvey(raw: Partial<BoNoticeSurvey> | null | undefined): BoNoticeSurvey {
  const base = emptyBoNoticeSurvey();
  if (!raw || typeof raw !== 'object') return base;

  let purposes = Array.isArray(raw.purposes)
    ? raw.purposes.filter((p): p is BoNoticePurposeKind => p === 'identify' || p === 'maintain')
    : [];
  if (!purposes.length && raw.purpose) {
    if (raw.purpose === 'identify' || raw.purpose === 'maintain') purposes = [raw.purpose];
  }

  return {
    purposes,
    identifyOptions: Array.isArray(raw.identifyOptions) ? [...raw.identifyOptions] : [],
    maintainOptions: Array.isArray(raw.maintainOptions) ? [...raw.maintainOptions] : [],
    completed: !!raw.completed,
  };
}

export function hasPurpose(survey: BoNoticeSurvey, kind: BoNoticePurposeKind): boolean {
  return (normalizeSurvey(survey).purposes || []).includes(kind);
}

export const IDENTIFY_OPTIONS: BoNoticeSurveyOptionMeta[] = [
  {
    id: 'corporate_member',
    label: 'Corporate member (s.60C(1))',
    hint: 'Ask a corporate shareholder who the beneficial owner is.',
    docCode: 'BO01',
  },
  {
    id: 'individual_member',
    label: 'Individual member (s.60C(1))',
    hint: 'Ask an individual shareholder if they are the BO (or who is).',
    docCode: 'BO02',
  },
  {
    id: 'suspected_bo',
    label: 'Suspected beneficial owner (s.60C(2))',
    hint: 'Ask a person you believe is the BO to confirm.',
    docCode: 'BO03',
  },
  {
    id: 'person_who_may_know',
    label: 'Person who may know the BO (s.60C(3))',
    hint: 'Ask someone who is likely to know the BO’s identity.',
    docCode: 'BO04',
  },
];

export const MAINTAIN_OPTIONS: BoNoticeSurveyOptionMeta[] = [
  {
    id: 'change',
    label: 'Change of particulars (s.60C(5))',
    hint: 'You believe BO particulars on the register have changed.',
    docCode: 'BO05',
  },
  {
    id: 'confirm',
    label: 'Confirm particulars (s.60C(5))',
    hint: 'You want to confirm whether registered particulars are correct.',
    docCode: 'BO06',
  },
];

const IDENTIFY_MAP: Record<BoNoticeIdentifyOption, BoNoticeDocCode> = {
  corporate_member: 'BO01',
  individual_member: 'BO02',
  suspected_bo: 'BO03',
  person_who_may_know: 'BO04',
};

const MAINTAIN_MAP: Record<BoNoticeMaintainOption, BoNoticeDocCode> = {
  change: 'BO05',
  confirm: 'BO06',
};

/** 설문 답변으로 이번에 포함할 문서 코드 목록 (순서 고정). */
export function resolveIncludedDocs(survey: BoNoticeSurvey | null | undefined): BoNoticeDocCode[] {
  const s = normalizeSurvey(survey);
  if (!s.completed || !s.purposes.length) return [];
  const order = Object.keys(BO_NOTICE_DOCS) as BoNoticeDocCode[];
  const set = new Set<BoNoticeDocCode>();

  if (s.purposes.includes('identify')) {
    for (const o of s.identifyOptions || []) {
      const c = IDENTIFY_MAP[o];
      if (c) set.add(c);
    }
  }
  if (s.purposes.includes('maintain')) {
    for (const o of s.maintainOptions || []) {
      const c = MAINTAIN_MAP[o];
      if (c) set.add(c);
    }
  }

  return order.filter((c) => set.has(c));
}

export function isSurveyReady(survey: BoNoticeSurvey): boolean {
  const s = normalizeSurvey(survey);
  if (!s.purposes.length) return false;
  let ok = false;
  if (s.purposes.includes('identify')) {
    if ((s.identifyOptions?.length || 0) === 0) return false;
    ok = true;
  }
  if (s.purposes.includes('maintain')) {
    if ((s.maintainOptions?.length || 0) === 0) return false;
    ok = true;
  }
  return ok;
}

export function summarizeSurvey(survey: BoNoticeSurvey): string {
  const codes = resolveIncludedDocs(survey);
  if (!codes.length) return 'No notices selected';
  return codes.map((c) => BO_NOTICE_DOCS[c].label).join(' · ');
}
